// framework/client/components/DataWizard.js
import { ComponentBase } from "../core/ComponentBase.js";
import { DataForm } from "./DataForm.js"; // Assuming DataForm is in components
import { z } from "zod";

export class DataWizard extends ComponentBase {
    // framework/client/components/DataWizard.js

    static styles = `
        /* Basic styling - adjust as needed */
        .data-wizard {
            display: flex;
            flex-direction: column;
            height: 100%;
        }

        .wizard-header {
            padding: 1rem;
            border-bottom: 1px solid #dee2e6;
            display: flex; /* Use flexbox for header layout */
            justify-content: space-between; /* Space out title and step indicator */
            align-items: center; /* Vertically center items */
        }

        .wizard-title {
            font-size: var(--ab-text-lg); /* Larger font size for title */
            font-weight: bold;
        }

        .wizard-step-indicator {
            font-size: var(--ab-text-md);
        }

        .wizard-steps {
            display: flex; /* Keep this for larger screens */
            /* Add styles for step indicators (e.g., numbered circles, progress bar) */
        }

        .wizard-step {
        /* Style for individual step indicators */
        padding: 0.5rem;
        cursor: pointer;
        }
        .wizard-step.active {
        font-weight: bold;
        color: blue; /* Or your primary color */
        }
        .wizard-body {
            flex: 1;
            overflow: auto;
            padding: 1rem;
        }
        .wizard-footer {
            padding: 1rem;
            border-top: 1px solid #dee2e6;
            display: flex;
            justify-content: space-between;
        }


        /* Mobile Styles (Hide Step Tabs) */
        @media (max-width: 768px) { /* Adjust breakpoint as needed */
            .wizard-steps {
                display: none; /* Hide the step tabs */
            }
            .wizard-title {
                display: none;
            }
        }
`;

    constructor(config) {
        super(config);
        this.steps = config.steps || [];
        this.currentStepIndex = 0;
        this.data = {}; // Overall data object
        this.stepComponents = []; // Array to hold step component instances (DataForm or custom)
        this.wizardContainer = null;
        this.stepsContainer = null;
        this.bodyContainer = null;
        this.footerContainer = null;
        this.stepIndicator = null; // Added for step indicator
        this.saveToLocalStorage = config.saveToLocalStorage !== false; // Default to true
        this.storageKey = `data-wizard-${this.config.instanceId}`;
        this.applyStyles();
    }

    async initialize() {
        this.log.info("Initializing Data Wizard");

        if (this.saveToLocalStorage) {
            this.loadState();
        }

        for (const stepConfig of this.steps) {
            if (stepConfig.component === "DataForm") {
                const cfg = stepConfig.componentConfig || {};
                const fields = cfg.fields || {};
                const hasTemplate = typeof cfg.bodyTemplate === "string" && cfg.bodyTemplate.trim() !== "";
                const hasFields = Object.keys(fields).length > 0;

                // If it's *only* a template and has no form fields, treat it as static
                if (hasTemplate && !hasFields) {
                    this.stepComponents.push(null);
                }
                else {
                    // instantiate a DataForm as before
                    const formConfig = {
                        ...cfg,
                        appClient: this.appClient,
                        instanceId: `${this.config.instanceId}-${stepConfig.title
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`,
                        schema: this.getStepSchema(stepConfig),
                        actions: null,
                        hideHeader: true,
                    };
                    const form = new DataForm(formConfig);
                    await form.initialize();
                    this.stepComponents.push(form);
                }
            }
            else {
                // custom component placeholder for future
                this.stepComponents.push(null);
            }
        }
    }

    getStepSchema(stepConfig) {
        if (!this.config.schema) {
            return null; // No overall schema
        }

        if (stepConfig.componentConfig.fields && typeof stepConfig.componentConfig.fields === 'object') {
            // Correctly handle an object for fields
            const schemaFields = {};
            for (const fieldName in stepConfig.componentConfig.fields) {
                if (this.config.schema.shape[fieldName]) {
                    schemaFields[fieldName] = this.config.schema.shape[fieldName];
                }
            }
            return z.object(schemaFields);
        }

        return null;
    }


    async render() {
        this.wizardContainer = document.createElement("div");
        this.wizardContainer.classList.add("data-wizard");

        // --- Header ---
        const header = document.createElement("div");
        header.classList.add("wizard-header");

        const title = document.createElement("h2");
        title.classList.add("wizard-title");
        title.textContent = this.config.title || this.t("Data Wizard"); // Use a title from config, or a default
        header.appendChild(title);

        this.stepIndicator = document.createElement("div");
        this.stepIndicator.classList.add("wizard-step-indicator");
        header.appendChild(this.stepIndicator);

        this.wizardContainer.appendChild(header);
        // --- End Header ---


        this.stepsContainer = document.createElement("div");
        this.stepsContainer.classList.add("wizard-steps");
        this.wizardContainer.appendChild(this.stepsContainer);

        this.bodyContainer = document.createElement("div");
        this.bodyContainer.classList.add("wizard-body");
        this.wizardContainer.appendChild(this.bodyContainer);

        this.footerContainer = document.createElement("div");
        this.footerContainer.classList.add("wizard-footer");
        this.wizardContainer.appendChild(this.footerContainer);

        this.renderSteps(); // Render step indicators
        await this.renderCurrentStep(); // Render the initial step
        this.renderFooter();  // Render the footer (buttons)

        return this.wizardContainer;
    }
    renderSteps() {
        this.stepsContainer.innerHTML = ''; // Clear previous steps
        this.steps.forEach((step, index) => {
            const stepElement = document.createElement("div");
            stepElement.classList.add("wizard-step");
            stepElement.textContent = this.t(step.title);
            if (index === this.currentStepIndex) {
                stepElement.classList.add("active");
            }
            stepElement.addEventListener('click', () => this.gotoStep(index));
            this.stepsContainer.appendChild(stepElement);
        });

        // Update the step indicator
        this.stepIndicator.textContent = `${this.t("Step")} ${this.currentStepIndex + 1} ${this.t("of")} ${this.steps.length}: ${this.t(this.steps[this.currentStepIndex].title)}`;
    }

    async renderCurrentStep() {
        this.bodyContainer.innerHTML = "";

        const stepConfig = this.steps[this.currentStepIndex];
        const stepComponent = this.stepComponents[this.currentStepIndex];
        const cfg = stepConfig.componentConfig || {};

        // 1) If this step is a pure HTML template (and no DataForm instance),
        //    render the HTML and return immediately.
        if (!stepComponent && cfg.bodyTemplate) {
            const wrapper = document.createElement("div");
            wrapper.innerHTML = cfg.bodyTemplate;
            this.bodyContainer.appendChild(wrapper);
            return;
        }

        // 2) Otherwise assume it's a DataForm instance
        if (stepComponent) {
            // inject any saved data so far
            if (this.data) {
                stepComponent.setData(this.data);
            }
            const rendered = await stepComponent.render();
            this.bodyContainer.appendChild(rendered);
        }
    }




    renderFooter() {
        this.footerContainer.innerHTML = ''; // Clear previous buttons

        if (this.currentStepIndex > 0) {
            const prevButton = document.createElement("button");
            prevButton.textContent = this.t("Previous");
            prevButton.className = "btn btn-secondary"; // Use Bootstrap classes
            prevButton.addEventListener('click', () => this.previousStep());
            this.footerContainer.appendChild(prevButton);
        }

        if (this.currentStepIndex < this.steps.length - 1) {
            const nextButton = document.createElement("button");
            nextButton.textContent = this.t("Next");
            nextButton.className = "btn btn-primary"; // Use Bootstrap classes
            nextButton.addEventListener('click', () => this.nextStep());
            this.footerContainer.appendChild(nextButton);
        }

        if (this.currentStepIndex === this.steps.length - 1) {
            const submitButton = document.createElement("button");
            submitButton.textContent = this.t("Submit");
            submitButton.className = "btn btn-success"; // Use Bootstrap classes
            submitButton.addEventListener('click', () => this.submit());
            this.footerContainer.appendChild(submitButton);
        }
    }

    async gotoStep(index) {
        if (index < 0 || index >= this.steps.length) {
            return;
        }

        // Only validate & collect data if there *is* a DataForm component there
        const currentForm = this.stepComponents[this.currentStepIndex];
        if (currentForm && currentForm.config.mode === "edit") {
            const valid = await currentForm.validate();
            if (!valid) {
                this.log.warn("Validation failed on step", this.currentStepIndex);
                return;
            }
            this.data = { ...this.data, ...currentForm.getData() };
        }

        this.currentStepIndex = index;
        this.renderSteps();
        await this.renderCurrentStep();
        this.renderFooter();

        if (this.saveToLocalStorage) {
            this.saveState();
        }
    }


    async previousStep() {
        await this.gotoStep(this.currentStepIndex - 1);
    }

    async nextStep() {
        await this.gotoStep(this.currentStepIndex + 1);
    }

    async submit() {
        // Final validation (optional, as we validate on each step)
        // Collect data from all steps (if needed, though we're already doing it in gotoStep)

        // Emit a 'submit' event with the complete data
        this.emit('submit', this.data);
    }

    renderSummary() {
        // Basic example: create a preformatted JSON display
        const summaryContainer = document.createElement('div');
        summaryContainer.classList.add('wizard-summary');

        const title = document.createElement('h3');
        title.textContent = 'Summary';
        summaryContainer.appendChild(title);

        const pre = document.createElement('pre');
        pre.textContent = JSON.stringify(this.data, null, 2);
        summaryContainer.appendChild(pre);

        return summaryContainer.outerHTML;
    }

    setData(data) {
        this.saveState(data);
    }

    saveState() {
        const state = {
            currentStepIndex: this.currentStepIndex,
            data: this.data,
        };
        localStorage.setItem(this.storageKey, JSON.stringify(state));
    }

    loadState() {
        const storedState = localStorage.getItem(this.storageKey);
        if (storedState) {
            const state = JSON.parse(storedState);
            this.currentStepIndex = state.currentStepIndex || 0;
            this.data = state.data || {};
        }
    }

    clearState() {
        localStorage.removeItem(this.storageKey);
    }

    destroy() {
        // Clean up event listeners, etc.
        // Destroy step component instances
        for (const component of this.stepComponents) {
            if (component && typeof component.destroy === 'function') {
                component.destroy();
            }
        }
        this.stepComponents = [];
        super.destroy();
    }
}